import {
  AttachmentQueue,
  type AttachmentErrorHandler,
  type RemoteStorageAdapter,
} from "@powersync/web"
import { supabase } from "@fieldbook/common/supabase"
import { bucketForPhotoPath } from "@fieldbook/common/lib/photoBuckets"
import {
  generateThumbnail,
  THUMBNAIL_PRESET,
  thumbnailPathFor,
} from "@fieldbook/common/lib/photoThumbnails"
import { powerSyncDb } from "./db"
import {
  isLocalDataResetInProgress,
  isPhotoTableAuthoritative,
} from "./attachments"
import {
  AttachmentPhotoDeletedError,
  AttachmentPhotoNotYetSyncedError,
  isPermanentAttachmentUploadError,
} from "./attachmentErrors"
import {
  projectPhotoCondition,
  projectPhotoParameters,
} from "./projectPhotoQuery"
import { createTransferBackoff, TransferBackoffSkip } from "./transferBackoff"
import { requeueMissingDownloads } from "./requeueMissingDownloads"
import { getActiveProject } from "@/hooks/useActiveProject"
import { getLiveSessionWithTimeout } from "@/lib/auth/liveSession"
import { createAttachmentStorage } from "@/platform"

/**
 * Stored photo thumbnails on mobile (plans/019).
 *
 * A second instance of PowerSync's AttachmentQueue, keyed by photo id, with
 * its own table (`thumbnail_attachments`) and file store. It never shares
 * state with the originals' queue, so a thumbnail problem can't hold up,
 * park or surface as an original's upload failure.
 *
 * - Upload: the thumbnail is generated when a photo is captured and saved
 *   here as QUEUED_UPLOAD. Uploading stores the bytes, then publishes
 *   `photos.thumbnail_path`, which PowerSync sends after the photo's insert.
 * - Download: published thumbnails in the active project are watched and
 *   downloaded, so grids work offline.
 * - Cleanup: the SDK archives thumbnails that leave the watched set and
 *   evicts them past the cache limit.
 *
 * Thumbnails are best effort, like the web upload: when one can't be made
 * or is refused, the photo shows its original and the backfill script
 * publishes a thumbnail later.
 */

export const THUMBNAIL_TABLE = "thumbnail_attachments"

// Its own directory (native) or database (PWA), apart from the originals, so
// clearing this queue can't touch them.
const localStorage = createAttachmentStorage("fieldbook-photo-thumbnails")

let storageReady: Promise<void> | null = null

/**
 * Display hooks and capture can touch storage before the queue has started,
 * so they initialize it first. Initializing only opens the database.
 */
const ensureStorage = () => (storageReady ??= localStorage.initialize())

const transferBackoff = createTransferBackoff()

async function requireLiveSession(action: "upload" | "download") {
  const session = await getLiveSessionWithTimeout()
  if (!session) throw new Error(`No session — cannot ${action} thumbnail`)
}

const remoteStorage: RemoteStorageAdapter = {
  async uploadFile(fileData, attachment) {
    if (transferBackoff.isBackingOff(attachment.id)) {
      throw new TransferBackoffSkip(attachment.id)
    }
    await requireLiveSession("upload")

    const row = await powerSyncDb.getOptional<{ file_path: string }>(
      "SELECT file_path FROM photos WHERE id = ?",
      [attachment.id],
    )
    if (!row?.file_path) {
      if (!isPhotoTableAuthoritative()) {
        throw new AttachmentPhotoNotYetSyncedError(attachment.id)
      }
      throw new AttachmentPhotoDeletedError(attachment.id)
    }

    const path = thumbnailPathFor(row.file_path, attachment.id)
    const bucket = bucketForPhotoPath(row.file_path)
    const { error } = await supabase.storage
      .from(bucket)
      .upload(path, new Blob([fileData], { type: THUMBNAIL_PRESET.mimeType }), {
        contentType: THUMBNAIL_PRESET.mimeType,
        upsert: true,
      })
    if (error) throw error
    transferBackoff.clear(attachment.id)

    // Mirrors the original's upload: a delete that landed during the
    // transfer already ran its storage cleanup, so remove what we just wrote.
    const stillExists = await powerSyncDb.getOptional(
      "SELECT id FROM photos WHERE id = ?",
      [attachment.id],
    )
    if (!stillExists) {
      if (!isPhotoTableAuthoritative()) {
        throw new AttachmentPhotoNotYetSyncedError(attachment.id)
      }
      const { error: removeError } = await supabase.storage
        .from(bucket)
        .remove([path])
      if (removeError) {
        console.warn(
          `[Thumbnails] Couldn't remove upload for deleted photo ${attachment.id}:`,
          removeError.message,
        )
      }
      throw new AttachmentPhotoDeletedError(attachment.id)
    }

    // The bytes are stored, so readiness can be published.
    await powerSyncDb.execute(
      `UPDATE photos SET thumbnail_path = ?
       WHERE id = ? AND thumbnail_path IS NOT ?`,
      [path, attachment.id, path],
    )
  },

  async downloadFile(attachment) {
    if (transferBackoff.isBackingOff(attachment.id)) {
      throw new TransferBackoffSkip(attachment.id)
    }
    await requireLiveSession("download")

    const row = await powerSyncDb.getOptional<{ thumbnail_path: string }>(
      "SELECT thumbnail_path FROM photos WHERE id = ?",
      [attachment.id],
    )
    if (!row?.thumbnail_path) {
      throw new Error(`No published thumbnail for photo ${attachment.id}`)
    }
    const { data, error } = await supabase.storage
      .from(bucketForPhotoPath(row.thumbnail_path))
      .download(row.thumbnail_path)
    if (error) throw error
    transferBackoff.clear(attachment.id)
    return data.arrayBuffer()
  },

  // Never queued: deleting a photo removes its stored thumbnail server side.
  async deleteFile() {},
}

/**
 * Back off a failed transfer. Offline failures don't count: the SDK retries
 * every pass and as soon as the connection returns, and a long offline
 * stretch mustn't leave thumbnails waiting hours after reconnecting.
 */
function recordFailure(id: string, direction: string, error: unknown) {
  if (!navigator.onLine) return
  const { failures, delayMs } = transferBackoff.recordFailure(id)
  console.warn(
    `[Thumbnails] ${direction} failed for ${id} (attempt ${failures}; next retry in ${Math.round(delayMs / 60000)}m):`,
    error,
  )
}

const errorHandler: AttachmentErrorHandler = {
  async onDownloadError(attachment, error) {
    if (!(error instanceof TransferBackoffSkip)) {
      recordFailure(attachment.id, "Download", error)
    }
    return true
  },
  async onUploadError(attachment, error) {
    if (error instanceof TransferBackoffSkip) return true
    if (error instanceof AttachmentPhotoNotYetSyncedError) return true
    if (error instanceof AttachmentPhotoDeletedError) return false
    // Back off even when giving up: an archived thumbnail is re-queued if its
    // photo is published elsewhere, and shouldn't retry straight away.
    recordFailure(attachment.id, "Upload", error)
    // A refused thumbnail is archived; the photo keeps showing its original.
    return !isPermanentAttachmentUploadError(error)
  },
  async onDeleteError() {
    return true
  },
}

/**
 * The SDK's stopSync aborts between items but doesn't wait for the one in
 * flight, so a download could save bytes and recreate its row after a reset
 * has cleared them. This stopSync waits for that, and for pending generation.
 */
class DrainingAttachmentQueue extends AttachmentQueue {
  private readonly active = new Set<Promise<unknown>>()

  track<T>(work: Promise<T>): Promise<T> {
    this.active.add(work)
    const untrack = () => this.active.delete(work)
    work.then(untrack, untrack)
    return work
  }

  override syncStorage(): Promise<void> {
    return this.track(
      this.withAttachmentContext((context) =>
        requeueMissingDownloads(context.db, context.tableName),
      ).then(() => super.syncStorage()),
    )
  }

  override async stopSync(): Promise<void> {
    await super.stopSync()
    await Promise.allSettled([...this.active])
  }
}

export const thumbnailQueue = new DrainingAttachmentQueue({
  db: powerSyncDb,
  tableName: THUMBNAIL_TABLE,
  localStorage,
  remoteStorage,
  errorHandler,
  // React quickly when a just-captured thumbnail is queued.
  syncThrottleDuration: 1000,
  watchAttachments: (onUpdate, signal) => {
    const projectId = getActiveProject()
    const query = projectId
      ? `SELECT p.id FROM photos p
         WHERE p.thumbnail_path IS NOT NULL
         AND ${projectPhotoCondition("p")}`
      : "SELECT id FROM photos WHERE 0"
    const parameters = projectId ? projectPhotoParameters(projectId) : []

    powerSyncDb.watch(
      query,
      parameters,
      {
        onResult: async (result) => {
          const rows: { id: string }[] = result.rows?._array ?? []
          await onUpdate(rows.map(({ id }) => ({ id, fileExtension: "jpg" })))
        },
      },
      { signal },
    )
  },
  downloadAttachments: true,
  // ~70 KB each, so thumbnails from past projects stay viewable offline.
  archivedCacheLimit: 2000,
})

let generation: Promise<void> = Promise.resolve()

/**
 * Generate a just-captured photo's thumbnail and queue it for upload. Runs
 * one at a time, since each decode holds a full-resolution bitmap in memory.
 * Never rejects: on failure the photo stays original-only.
 */
export function queuePhotoThumbnail(
  photoId: string,
  original: Blob,
): Promise<void> {
  generation = thumbnailQueue.track(
    generation.then(async () => {
      try {
        const thumbnail = await generateThumbnail(original)
        // The reset is clearing thumbnails; don't write one back.
        if (isLocalDataResetInProgress()) return
        await ensureStorage()
        await thumbnailQueue.saveFile({
          id: photoId,
          data: await thumbnail.arrayBuffer(),
          fileExtension: "jpg",
          mediaType: THUMBNAIL_PRESET.mimeType,
        })
      } catch (error) {
        console.warn(`[Thumbnails] Couldn't generate for ${photoId}:`, error)
      }
    }),
  )
  return generation
}

/**
 * Read a photo's local thumbnail. Throws when it can't be read, so callers
 * retry rather than caching a miss.
 */
export async function readThumbnailFile(
  localUri: string,
): Promise<ArrayBuffer> {
  await ensureStorage()
  return localStorage.readFile(localUri)
}
